import { ApolloProvider } from "@apollo/react-hooks";
import {
  UserProps,
  UserProvider2,
} from "@customer-tools-web/component-authentication";
import { useBraze } from "@customer-tools-web/component-header-footer";
import headerLocales from "@customer-tools-web/component-header-footer/lib/lang";
import { useI18N, I18NProvider } from "@customer-tools-web/component-i18n";
import { theme, GlobalFontOverrides } from "@customer-tools-web/component-ui";
import { GAProvider } from "@customer-tools-web/google-analysis-tracking";
import { MarketConfigProvider } from "@customer-tools-web/market-extension-resolver";
import * as Sentry from "@sentry/browser";
import { createBrowserHistory } from "history";
import { get, merge } from "lodash";
import * as React from "react";
import ReactDOM from "react-dom";
import { Router } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { client, getUMEndPoint } from "./apollo";
import App from "./app";
import { ScreenSizeProvider } from "./components/common/hoc/withScreenSize";
import { UnSavedChangesErrorModal } from "./components/common/hoc/withUnsavedChangeHandler";
import config from "./config/config";
import { GlobalStyles } from "./globalStyles";
import "./hacks/dynamic-vh";
import { isMobile } from "./hacks/mark-browsers";
import langLocales from "./lang";
import { HeaderProvider } from "./components/component-header/contexts/HeaderContext";

if (process.env.REACT_APP_ENABLE_SENTRY) {
  Sentry.init({
    release: "customer-tools-web@" + process.env.GIT_BUILD_NO,
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
  });
}

const root = document.getElementById("root");

const getUserConfirmation = (
  message: string,
  callback: (ok: boolean) => void
) => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const decorateCallback = (ok: boolean) => {
    callback(ok);
    container.remove();
  };
  ReactDOM.render(
    <Router history={customizeHistory}>
      <I18NProvider resources={merge({}, headerLocales, langLocales)}>
        <UnSavedChangesErrorModal
          callback={decorateCallback}
          message={message}
          visible={true}
        />
      </I18NProvider>
    </Router>,
    container
  );
};
const customizeHistory = createBrowserHistory({
  getUserConfirmation: getUserConfirmation,
});

function MainContent() {
  const [verified, setVerified] = React.useState(false);
  const braze = useBraze();

  React.useEffect(() => {
    braze?.init();
  }, []);

  function init(data: {} | { viewer: UserProps["viewer"] }) {
    checkUserEmailVerified(data);

    if (data && "viewer" in data) {
      configureSentryContext(data);
      braze?.login(data.viewer);
    }
  }

  function checkUserEmailVerified(data: {} | { viewer: UserProps["viewer"] }) {
    const viewer = get(data, "viewer");

    if (!viewer) {
      return;
    }
    const isCurrentPage = /users\/email-not-verified/.test(
      window.location.href
    );

    if (!isCurrentPage && viewer.id && !viewer.emailVerified) {
      window.location.href = getUMEndPoint() + "/email-not-verified";
    } else if (!verified) {
      setVerified(true);
    }
  }

  function configureSentryContext(data: { viewer: UserProps["viewer"] }) {
    Sentry.configureScope((scope) => {
      scope.setUser({
        email: data.viewer.email!,
        reaId: data.viewer.spotReaId,
        role: data.viewer.roleCodes!.map((o) => o.label),
      });
    });
  }

  const { locale } = useI18N();
  return (
    <Router history={customizeHistory}>
      <MarketConfigProvider config={config}>
        <I18NProvider resources={merge({}, headerLocales, langLocales)}>
          <ThemeProvider theme={{ ...theme, isMobile: `${isMobile()}` }}>
            <UserProvider2 locale={locale} onRequestCompleted={init}>
              {verified && (
                <ScreenSizeProvider>
                  <HeaderProvider>
                    <GlobalFontOverrides />
                    <GlobalStyles />
                    <App />
                  </HeaderProvider>
                </ScreenSizeProvider>
              )}
            </UserProvider2>
          </ThemeProvider>
        </I18NProvider>
      </MarketConfigProvider>
    </Router>
  );
}

if (root !== null) {
  ReactDOM.render(
    <GAProvider id={config.GTMContainerId}>
      <ApolloProvider client={client}>
        <MainContent />
      </ApolloProvider>
    </GAProvider>,
    root
  );
}
